import { CLERK_CREDENTIAL_ERROR_NAME } from '@lib/clerk/errors/clerk-credential-error';
import { isBrowserFetchTransportMessage } from '@lib/shared/utils/is-browser-fetch-transport-error';
import { isTransientNetworkError } from '@lib/shared/utils/is-transient-network-error';
import { isTransientPostgresPoolError } from '@lib/shared/utils/is-transient-postgres-pool-error';
import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Shared input for `Sentry.consoleLoggingIntegration({ levels: [...] })`.
 * Used by all three Sentry init files (server / edge / client).
 */
export const SENTRY_CONSOLE_LOG_LEVELS = [
  'log',
  'warn',
  'error',
  'info',
] as const;

const supabaseHost = (() => {
  const raw = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!raw) return undefined;
  try {
    return new URL(raw).hostname;
  } catch {
    return undefined;
  }
})();

const nutrientEngineHost = (() => {
  const raw = process.env.NEXT_PUBLIC_NUTRIENT_DOCUMENT_ENGINE_URL;
  if (!raw) return undefined;
  try {
    return new URL(raw).hostname;
  } catch {
    return undefined;
  }
})();

/**
 * True for outbound URLs that hit our Supabase project's PostgREST table
 * endpoints. Used by `httpIntegration` / `nativeNodeFetchIntegration`
 * (server) and `browserTracingIntegration` (client) to skip generic span
 * creation — `Sentry.instrumentSupabaseClient` (called per client in
 * src/lib/supabase/base-client.ts) emits richer `db` spans for the same
 * requests.
 *
 * SCOPE: matches `/rest/v1/<table>...` but EXCLUDES `/rest/v1/rpc/<fn>`.
 * The Sentry integration only patches `SupabaseClient.prototype.from`, which
 * covers select/insert/upsert/update/delete on tables. It does NOT patch
 * `.rpc()`, `.storage.*`, `.functions.*`, or `.channel()`. Filtering those
 * paths would silence them entirely (no replacement span); let them keep
 * their default fetch/http spans.
 *
 * Host is derived from `NEXT_PUBLIC_SUPABASE_URL` so local dev
 * (`http://127.0.0.1:54321`), self-hosted, and custom-domain Supabase
 * deployments are handled uniformly — not just `*.supabase.co`.
 */
export function isSupabaseUrl(url: string): boolean {
  if (!supabaseHost) return false;
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== supabaseHost) return false;
    const path = parsed.pathname;
    if (!path.startsWith('/rest/')) return false;
    // Exclude .rpc() calls — the integration doesn't patch them, and
    // matching `/rest/v\d+/rpc/<fn>` (path segment required) avoids a
    // false-positive on a table literally named `rpc` queried at
    // `/rest/v1/rpc?select=...`.
    if (/^\/rest\/v\d+\/rpc\/[^/]+/.test(path)) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * True for outbound URLs that hit the Nutrient Document Engine's Instant
 * collaboration sync endpoints (`/i/d/<documentId>/h/<handle>/...`). The
 * Nutrient Web SDK long-polls these endpoints for the entire time a
 * document is open, which keeps the `browserTracingIntegration` idle span
 * alive on `/deals/.../packages/.../docs` and
 * `/templates/.../packages/.../docs` until it hits Sentry's 30s
 * `finalTimeout` cap. The transaction then reports
 * `status: deadline_exceeded` and an artificially inflated duration that
 * does not reflect user-perceived load time.
 *
 * SCOPE: only the Instant sync path prefix (`/i/`) on the configured
 * Document Engine host is excluded from auto-span creation. Other Document
 * Engine endpoints (e.g. server-side document upload, JWTs) are server-to-
 * server and not affected by this client-side filter, but are kept matchable
 * by the host check so that a future expansion is easy. The PUBLIC engine
 * URL is required: when unset (preview / local dev), we fall back to a no-op
 * so we don't accidentally suppress same-origin spans.
 */
export function isNutrientInstantSyncUrl(url: string): boolean {
  if (!nutrientEngineHost) return false;
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== nutrientEngineHost) return false;
    return parsed.pathname.startsWith('/i/');
  } catch {
    return false;
  }
}

/**
 * True for the Nutrient Web SDK's iOS/WebKit text-selection crash:
 * `IndexSizeError` thrown by native `Range.setStart` inside Nutrient's
 * deferred `_checkForTextSelection` timer, which fires after the viewer
 * is unloaded on signing completion. The signature is already saved and
 * validated before the throw, so this is post-commit teardown noise with
 * no user impact — confined to WebKit (iPhone-dominant).
 *
 * SCOPE: both conditions required — exception `type` is `IndexSizeError`
 * AND a stackframe originates in `nutrient-viewer.js`. A genuine
 * `IndexSizeError` from first-party code keeps reporting.
 *
 * Tracking: PORTAL-85 (filed upstream with Nutrient — their `unload()`
 * does not cancel the pending selection timer).
 */
export function isNutrientTextSelectionIndexError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(
    value =>
      value.type === 'IndexSizeError' &&
      (value.stacktrace?.frames ?? []).some(frame =>
        frame.filename?.includes('nutrient-viewer.js')
      )
  );
}

/**
 * True for Nutrient Web SDK's page-annotation render race during viewer
 * teardown: `TypeError: Cannot read properties of null (reading
 * 'renderPageAnnotations')` (Chrome) / equivalent null-property messages
 * that name `renderPageAnnotations`. Fires as an unhandledrejection from
 * an internal Nutrient promise after `NutrientViewer.unload()` while
 * annotation rendering is still in flight — e.g. opening an Insights PDF
 * preview and navigating away within ~1s of load.
 *
 * Pure first-party code cannot catch this (no app frames; mechanism is
 * `onunhandledrejection`). Document/data are fine; user impact is a
 * transient console rejection after leaving the viewer.
 *
 * SCOPE: `TypeError` whose message mentions `renderPageAnnotations` AND
 * a stackframe originates in Nutrient (`nutrient-viewer.js` or
 * `nutrient-viewer-lib`). Other TypeErrors from Nutrient keep reporting.
 *
 * Tracking: PORTAL-1CX (same teardown-noise class as PORTAL-85).
 */
function isNutrientViewerStackFrame(filename: string | undefined): boolean {
  if (!filename) return false;
  return (
    filename.includes('nutrient-viewer.js') ||
    filename.includes('nutrient-viewer-lib')
  );
}

export function isNutrientRenderPageAnnotationsTeardownError(
  event: ErrorEvent
): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value => {
    if (value.type !== 'TypeError') return false;
    if (
      typeof value.value !== 'string' ||
      !value.value.includes('renderPageAnnotations')
    ) {
      return false;
    }
    return (value.stacktrace?.frames ?? []).some(frame =>
      isNutrientViewerStackFrame(frame.filename)
    );
  });
}

/**
 * Per-browser `DOMException` messages for `AbortController.abort()` called
 * without a reason, as Nutrient does on teardown. WebKit says
 * `Fetch is aborted` (PORTAL-1FZ); Chromium says
 * `signal is aborted without reason` (PORTAL-1RZ).
 */
const NUTRIENT_FETCH_ABORT_MESSAGES = new Set([
  'Fetch is aborted',
  'signal is aborted without reason',
]);

/**
 * True for Nutrient Web SDK fetch abort noise during viewer teardown:
 * `AbortError` from internal `cancel()` paths when `NutrientViewer.unload()`
 * or navigation aborts in-flight Document Engine sync fetches. Fires as an
 * unhandledrejection from `nutrient-viewer.js` with no first-party frames —
 * same teardown-noise class as PORTAL-85 / PORTAL-1CX.
 *
 * SCOPE: `AbortError` whose message is a browser's reason-less abort string
 * (see `NUTRIENT_FETCH_ABORT_MESSAGES`) AND a stackframe originates in
 * Nutrient (`nutrient-viewer.js` or `nutrient-viewer-lib`). First-party
 * `AbortError`s (TanStack cancel, autosave supersede, unmount) keep
 * reporting via other suppress paths.
 *
 * Tracking: PORTAL-1FZ (Safari), PORTAL-1RZ (Chrome).
 */
export function isNutrientFetchAbortTeardownError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value => {
    if (value.type !== 'AbortError') return false;
    if (
      typeof value.value !== 'string' ||
      !NUTRIENT_FETCH_ABORT_MESSAGES.has(value.value)
    ) {
      return false;
    }
    return (value.stacktrace?.frames ?? []).some(frame =>
      isNutrientViewerStackFrame(frame.filename)
    );
  });
}

/**
 * True for Nutrient Instant / Document Engine browser `fetch` transport
 * failures that leak as unhandled rejections from `nutrient-viewer.js`
 * (`c.runItem` queue). Chrome reports `TypeError: Failed to fetch`; Sentry's
 * fetch instrumentation often appends the engine host
 * (`Failed to fetch (docs.services…)`). Firefox / Safari use their usual
 * transport strings. There is no app catch site — Instant sync continues
 * after a blip, and first-party `handleLoadError` only sees wrapped
 * `PSPDFKitError`s at init time.
 *
 * SCOPE: `TypeError` whose message is a browser transport failure AND either
 * a Nutrient viewer stack frame or a parenthetical host matching the
 * configured Document Engine. First-party `Failed to fetch` (TanStack,
 * `apiFetch`) keep reporting via those paths unless they already match
 * `isBrowserFetchTransportError`.
 *
 * Tracking: PORTAL-1JA (raw TypeError twin of PORTAL-1AG / PORTAL-5Z).
 */
export function isNutrientDocumentEngineFetchError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value => {
    if (value.type !== 'TypeError') return false;
    if (typeof value.value !== 'string') return false;
    if (!isBrowserFetchTransportMessage(value.value)) return false;

    const hasNutrientStack = (value.stacktrace?.frames ?? []).some(frame =>
      isNutrientViewerStackFrame(frame.filename)
    );
    if (hasNutrientStack) return true;

    return Boolean(
      nutrientEngineHost && value.value.includes(`(${nutrientEngineHost})`)
    );
  });
}

/**
 * Environmental Fingerprint Pro agent failures that the SDK surfaces as
 * unhandled rejections when its CDN script (`fpnpmcdn.net`) cannot load.
 * Capture hooks already soft-fail (`null` / synthetic visitor ids); login
 * and signing continue. Dominated by Firefox Enhanced Tracking Protection
 * / ad blockers (PORTAL-9V: ~92% Firefox).
 *
 * SCOPE: `FingerprintError` whose message matches a known load/network
 * failure. Misconfiguration errors (`api_key_missing`,
 * `endpoints_misconfigured`, etc.) keep reporting.
 *
 * Tracking: PORTAL-9V.
 */
const FINGERPRINT_AGENT_LOAD_MESSAGES = new Set([
  'Failed to load the JS script of the agent',
  'Blocked by CSP',
  'Network connection error',
  'Network request aborted',
  'Client timeout',
]);

export function isFingerprintAgentLoadError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(
    value =>
      value.type === 'FingerprintError' &&
      typeof value.value === 'string' &&
      FINGERPRINT_AGENT_LOAD_MESSAGES.has(value.value)
  );
}

/**
 * Google Identity Services (GSI) client noise from Clerk's Google One Tap.
 * When `/gsi/status` returns a non-JSON 400 (Safari ITP, bots, FedCM
 * transition, blocked third-party cookies), the minified GSI script throws
 * cryptic `Error`s (e.g. message `"oa"`) from `gsi/client`. Sentry's XHR
 * instrumentation captures them as unhandled; email/password and the
 * explicit Google button remain usable.
 *
 * SCOPE: stack frame filename includes `gsi/client`. Message text alone is
 * too short/minified to match safely, and first-party Errors with the same
 * short string keep reporting.
 *
 * Tracking: PORTAL-17R.
 */
export function isGoogleGsiClientError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value =>
    (value.stacktrace?.frames ?? []).some(frame =>
      frame.filename?.includes('gsi/client')
    )
  );
}

/**
 * Clerk's `@clerk/shared` loader injects `clerk.browser.js` from the
 * Frontend API host (`/npm/@clerk/clerk-js@…/dist/clerk.browser.js`).
 * When the `<script>` onerror / timeout fires, it rejects with
 * `ClerkRuntimeError` `code="failed_to_load_clerk_js"`. Production
 * minifies the class name to `e`; Sentry still records the formatted
 * message. Login / signup already treat this as degraded
 * (`useClerkStatus` 8s timeout → `AuthUnavailable`); the React status
 * often stays `loading`, so the runtime error is expected noise.
 *
 * SCOPE: exception message includes Clerk's load-failure code or the
 * `Failed to load Clerk JS` prefix. Other ClerkRuntimeErrors (auth,
 * FAPI, satellite) keep reporting.
 *
 * Tracking: PORTAL-1H9.
 */
const CLERK_JS_LOAD_FAILURE = /Failed to load Clerk JS|failed_to_load_clerk_js/;

export function isClerkJsLoadError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(
    value =>
      typeof value.value === 'string' && CLERK_JS_LOAD_FAILURE.test(value.value)
  );
}

/**
 * ClerkJS wraps browser `fetch` transport failures (offline blip, tab sleep,
 * flaky Wi‑Fi) as `Error: ClerkJS: Network error at "<fapi url>" - TypeError:
 * Failed to fetch …` and rejects without an app-owned catch site. Sentry
 * captures these via `onunhandledrejection`. Session `/touch` heartbeats are
 * the dominant path (PORTAL-SX / PORTAL-RM); Chrome says `Failed to fetch`,
 * Firefox `NetworkError when attempting to fetch resource.`, Safari / Chrome
 * Mobile iOS `Load failed`. WebKit also surfaces NSURLError strings for the
 * same transport failures: `The network connection was lost.`, `The Internet
 * connection appears to be offline.`, `The request timed out.`.
 *
 * Distinct from `clerk_offline` / `ClerkOfflineError` (PORTAL-178; see
 * src/lib/clerk/utils/is-clerk-offline-error.ts), which our session bridge
 * already soft-handles on `getToken`. This filter covers the SDK's own
 * unhandled rejection, which we cannot catch in-app.
 *
 * SCOPE: message must start with ClerkJS's network-error prefix AND embed a
 * known browser transport failure. Auth/API denials (401/403/422) use
 * different Clerk error shapes and keep reporting. Prefer also seeing a
 * `clerk.browser.js` / `clerk-js` frame when a stack is present.
 *
 * Tracking: PORTAL-SX, PORTAL-RM (also covers PORTAL-KA staging sibling).
 */
const CLERK_JS_TRANSPORT_FAILURE =
  /Failed to fetch|NetworkError when attempting to fetch|Load failed|The network connection was lost|The Internet connection appears to be offline|The request timed out/i;

export function isClerkJsNetworkError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value => {
    if (typeof value.value !== 'string') return false;
    if (!value.value.startsWith('ClerkJS: Network error')) return false;
    if (!CLERK_JS_TRANSPORT_FAILURE.test(value.value)) return false;

    const frames = value.stacktrace?.frames ?? [];
    if (frames.length === 0) return true;
    return frames.some(
      frame =>
        frame.filename?.includes('clerk.browser.js') ||
        frame.filename?.includes('clerk-js')
    );
  });
}

/**
 * Expected Clerk login credential denials (wrong password, unknown
 * account, 2FA-required, incomplete status). Already shown inline on
 * `/login`; MutationCache suppresses them via
 * `meta.suppressSentryErrorNames`, but react-hook-form rethrows
 * `onSubmit` failures so an uncaught `mutateAsync` rejection still
 * reaches Sentry's global `onunhandledrejection` handler.
 *
 * SCOPE: exception `type` is exactly `ClerkCredentialError`. Plain
 * `Error`s from unexpected sign-in failures keep reporting.
 *
 * Tracking: PORTAL-VG (TanStack suppress), PORTAL-181 / PORTAL-19S
 * (unhandledrejection).
 */
export function isClerkCredentialError(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value => value.type === CLERK_CREDENTIAL_ERROR_NAME);
}

/**
 * Next.js throws `Error: failed to pipe response` from `pipe-readable.js`
 * when piping a streaming route body fails — typically because the browser
 * closed the connection (navigation, panel unmount, tab sleep) while an LLM
 * SSE response was still in flight. The chained causes are undici transport
 * noise (`TypeError: terminated`, `SocketError: other side closed`), not an
 * app bug.
 *
 * SCOPE: top-level message is exactly `failed to pipe response`, with either
 * a disconnect signature in the chained exceptions or a Next pipe stack frame.
 * Other pipe failures keep reporting.
 *
 * Tracking: PORTAL-1F2.
 */
export function isNextFailedToPipeResponseError(error: unknown): boolean {
  for (let cursor: unknown = error, depth = 0; depth < 8; depth++) {
    if (cursor == null || typeof cursor !== 'object') return false;

    const message = 'message' in cursor ? String(cursor.message) : '';
    if (message === 'failed to pipe response') return true;

    cursor = 'cause' in cursor ? cursor.cause : undefined;
  }
  return false;
}

function exceptionHasStreamDisconnectSignature(
  type: string | undefined,
  value: string | undefined
): boolean {
  if (type === 'AbortError' || type === 'ResponseAborted') return true;
  if (value === 'terminated') return true;
  if (type === 'SocketError' && value?.includes('other side closed')) {
    return true;
  }
  return false;
}

function exceptionHasNextPipeStackFrame(
  frames: { filename?: string }[] | undefined
): boolean {
  return (frames ?? []).some(frame => {
    const filename = frame.filename ?? '';
    return (
      filename.includes('pipe-readable') || filename.includes('send-response')
    );
  });
}

/**
 * Transient outbound network failures forwarded to Sentry via
 * `logError` / pinoIntegration — Supabase pooler blips
 * (`upstream connect error … delayed connect error: 111`),
 * undici `fetch failed`, socket hang-ups, etc. Infra noise,
 * not application faults.
 *
 * SCOPE: any exception value whose message (or `.cause` chain)
 * matches {@link isTransientNetworkError}. Genuine Postgres/RLS
 * errors keep reporting.
 *
 * Tracking: PORTAL-15J / PORTAL-1F5 cluster (Supabase PostgREST
 * rollout).
 */
export function isTransientNetworkSentryEvent(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value =>
    isTransientNetworkError(
      typeof value.value === 'string' ? new Error(value.value) : value
    )
  );
}

/**
 * Benign `pg` Pool idle-client disconnects forwarded to Sentry
 * via `logError` / pinoIntegration — Supabase recycles idle
 * connections (57P01 / admin command) while the request succeeds.
 *
 * Tracking: PORTAL-1GQ.
 */
export function isTransientPostgresPoolSentryEvent(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(value =>
    isTransientPostgresPoolError(
      typeof value.value === 'string' ? new Error(value.value) : value
    )
  );
}

export function isNextFailedToPipeResponseSentryEvent(
  event: ErrorEvent
): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  const hasFailedToPipe = values.some(
    value => value.value === 'failed to pipe response'
  );
  if (!hasFailedToPipe) return false;

  const hasDisconnectCause = values.some(value =>
    exceptionHasStreamDisconnectSignature(value.type, value.value)
  );
  if (hasDisconnectCause) return true;

  return values.some(value =>
    exceptionHasNextPipeStackFrame(value.stacktrace?.frames)
  );
}

function parseRate(value: string | undefined): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

const enabled = process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true';

/**
 * Outbound URLs that receive `sentry-trace` + `baggage` headers for distributed
 * tracing. Scoped to `/api/*` so browser → Next.js route handlers stay linked
 * without attaching trace headers to every same-origin asset/RSC fetch.
 *
 * Matching (per @sentry/browser): `/^\/api\//` is tested against the full URL
 * and, for same-origin browser requests, against the pathname — so relative
 * `fetch('/api/...')` is covered without matching third-party URLs that merely
 * contain `/api/` in the path. `localhost` covers local dev hosts.
 */
export const SENTRY_TRACE_PROPAGATION_TARGETS: (string | RegExp)[] = [
  'localhost',
  /^\/api\//,
];

export const sentryConfig = {
  enabled,
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  tracesSampleRate: parseRate(
    process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE
  ),
  replaySessionSampleRate: parseRate(
    process.env.NEXT_PUBLIC_SENTRY_REPLAY_SESSION_SAMPLE_RATE
  ),
  replayOnErrorSampleRate: parseRate(
    process.env.NEXT_PUBLIC_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE
  ),
  profilesSampleRate: parseRate(
    process.env.NEXT_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE
  ),
  enableLogs: process.env.SENTRY_ENABLE_LOGS === 'true',
} as const;
